/**
 * worker.service.ts
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import moment from "moment-timezone";

import { ChannelGroupMessage, ChannelMeetingMessage, ChannelMessage,
  ChannelNotificationTypes, ClientMessage } from "../models/broadcast";
import { ApnsPushInfo, HuddleRequestTimeout, NotificationType,
  PushNotificationGroupData, PushNotificationMessageData } from "../models/pushNotification";

import { http } from "./http.service";
import { ErrorInstruction, Executor } from "../models/executor";
import { ErrorCode, GroupError } from "../models/error";

export interface WorkerServiceDelegate {
  didReceiveNewMessage(): void,
  didStartHuddle(): void,
  showIncomingCall(accountID: string, groupID: string, groupname: string): void,
  didAnswerHuddle(): void,
  hideIncomingCall(accountID: string, groupID: string): void,
  navigateToGroup(accountID: string, groupID: string): void,
  doHuddleJoin(accountID: string, groupID: string): void
}
/*export interface WorkerGroupDelegate {
  handleWorkerGroupMessage(msg: WorkerMessage): Promise<void>
}*/

type msgListenerFn = (msgID: string, parent: string | undefined, accountID: string) => Promise<void>;
type groupListenerFn = (groupID: string, threadID?: string) => Promise<void>;
type remMsgListenerFn = (groupID: string, msgID: string, accountID: string, parentID?: string) => Promise<void>;
type huddleListenerFn = (groupID: string, accountID: string) => Promise<void>;
type meetingMsgListenerFn = (msg: ChannelMeetingMessage) => Promise<void>;
type reactionListenerFn = (msgID: string, reactionID: string, userID: string, contentID: string) => Promise<void>;
type reactionRemListenerFn = (msgID: string, contentID: string, userID: string) => Promise<void>;

type ListenerTypes = msgListenerFn | groupListenerFn | remMsgListenerFn | huddleListenerFn | meetingMsgListenerFn |
  reactionListenerFn | reactionRemListenerFn;

export interface WorkerService {
  delegate: WorkerServiceDelegate | undefined
  //groupDelegate: WorkerGroupDelegate | undefined
  alreadyHaveNotificationPermission: boolean,
  registerWorker(): Promise<ServiceWorkerRegistration | null>,
  updateWorker(): Promise<void>,
  activateNewWorker(): Promise<void>,
  destroy(): void,
  didLoadAuthed(): void,
  didShow(): void,
  didHide(): void,
  willUnload(): void,
  didChangeAccount(membershipID: string, accountID: string, userID: string): void,
  didLogin(userID: string): void,
  didLogout(): void,
  didStartMeetingSession(): void,
  didFinishMeetingSession(): void,
  askNotificationPermission(): Promise<string>,
  askSafariNotificationPermission(accessToken: string, deviceID: string): Promise<void>,
  subscribe(registration: ServiceWorkerRegistration | null): Promise<PushSubscription | ApnsPushInfo | undefined>,
  saveSubscription(subscription: PushSubscription): Promise<void>,

  on(type: "message", fn: msgListenerFn): void,
  on(type: "newgroup", fn: groupListenerFn): void,
  on(type: "group", fn: groupListenerFn): void,
  on(type: "thread", fn: groupListenerFn): void,
  on(type: "delgroup", fn: groupListenerFn): void,
  on(type: "delmsg", fn: remMsgListenerFn): void,
  on(type: "huddleStart", fn: huddleListenerFn): void,
  on(type: "huddleEnd", fn: huddleListenerFn): void,
  on(type: "mtgDecision", fn: meetingMsgListenerFn): void,
  on(type: "newWaiting", fn: meetingMsgListenerFn): void,
  on(type: "remWaiting", fn: meetingMsgListenerFn): void,
  on(type: "reaction", fn: reactionListenerFn): void,
  on(type: "remReaction", fn: reactionRemListenerFn): void,
  off(type: "message", fn: msgListenerFn): void,
  off(type: "newgroup", fn: groupListenerFn): void,
  off(type: "group", fn: groupListenerFn): void,
  off(type: "thread", fn: groupListenerFn): void,
  off(type: "delgroup", fn: groupListenerFn): void,
  off(type: "delmsg", fn: remMsgListenerFn): void,
  off(type: "huddleStart", fn: huddleListenerFn): void,
  off(type: "huddleEnd", fn: huddleListenerFn): void,
  off(type: "mtgDecision", fn: meetingMsgListenerFn): void,
  off(type: "newWaiting", fn: meetingMsgListenerFn): void,
  off(type: "remWaiting", fn: meetingMsgListenerFn): void,
  off(type: "reaction", fn: reactionListenerFn): void,
  off(type: "remReaction", fn: reactionRemListenerFn): void
}

class WorkerServiceImpl implements WorkerService {
  public delegate: WorkerServiceDelegate | undefined;
  //public groupDelegate: WorkerGroupDelegate | undefined;

  private listenerMap: Map<ChannelNotificationTypes, Array<ListenerTypes>>;
  private groupExecutors: Map<string, Executor<Date>>;

  get alreadyHaveNotificationPermission(): boolean {
    if ("safari" in window) {
      const permData = (window as any).safari.pushNotification.permission(secrets.SAFARI_PUSH_ID);
      if (!permData) { return false; }
      return permData.permission === "granted";
    }
    return "serviceWorker" in navigator && "PushManager" in window &&
        Notification && Notification.permission === "granted";
  }

  private channel: BroadcastChannel;
  private callChannel: BroadcastChannel;
  private lastUpdateCheck: Date | null;

  constructor() {
    this.delegate = undefined;
    this.lastUpdateCheck = null;
    //this.groupDelegate = undefined;
    this.subscribe = this.subscribe.bind(this); // passed to promise chain
    this.onMessage = this.onMessage.bind(this);
    this.onClientMessage = this.onClientMessage.bind(this);
    this.onCallMessage = this.onCallMessage.bind(this);

    this.listenerMap = new Map<ChannelNotificationTypes, Array<ListenerTypes>>();
    this.groupExecutors = new Map<string, Executor<Date>>();
    this.executorError = this.executorError.bind(this);

    this.channel = new BroadcastChannel("groupUpdates");
    this.channel.addEventListener("message", this.onMessage);
    navigator.serviceWorker.addEventListener("message", this.onClientMessage);

    this.callChannel = new BroadcastChannel("huddleUpdates");
    this.callChannel.addEventListener("message", this.onCallMessage);
  }

  destroy(): void {
    navigator.serviceWorker.addEventListener("message", this.onClientMessage);
    workerService = undefined;
    this.channel.close();
    this.callChannel.close();
  }

  on (type: ChannelNotificationTypes, fn: ListenerTypes) {
    let listeners = this.listenerMap.get(type);
    if (!listeners) {
      listeners = [];
      this.listenerMap.set(type, listeners);
    }
    listeners?.push(fn);
  }
  off (type: ChannelNotificationTypes, fn: ListenerTypes) {
    const listeners = this.listenerMap.get(type);
    const index = listeners?.indexOf(fn);
    if (listeners && index !== undefined && index >= 0) {
      listeners.splice(index, 1);
      this.listenerMap.set(type, listeners);
    }
  }

  didLoadAuthed(): void {
    this.sendSimpleCmd("didLoad");
  }
  didShow(): void {
    this.sendSimpleCmd("didShow");
  }
  didHide(): void {
    this.sendSimpleCmd("didHide");
  }
  willUnload(): void {
    this.sendSimpleCmd("willUnload");
  }
  didChangeAccount(membershipID: string, accountID: string, userID: string): void {
    navigator.serviceWorker.controller?.postMessage({
      cmd: "acctChange",
      membershipID,
      accountID,
      userID
    });
  }
  didLogin(userID: string): void {
    navigator.serviceWorker.controller?.postMessage({
      cmd: "login",
      userID: userID
    });
  }
  didLogout(): void {
    this.sendSimpleCmd("logout");
  }
  didStartMeetingSession(): void {
    this.sendSimpleCmd("mtgStart");
  }
  didFinishMeetingSession(): void {
    this.sendSimpleCmd("mtgEnd");
  }

  executorForGroup(groupID: string): Executor<Date> {
    const executor = this.groupExecutors.get(groupID) ||
      new Executor<Date>(groupID, (a: Date, b: Date): boolean => {
        return a <= b;
      },
        this.executorError);
    this.groupExecutors.set(groupID, executor);
    return executor;
  }

  async executorError(groupID: string, err: Error, executor: Executor<Date>): Promise<ErrorInstruction> {
    console.error("Error from executor, group:", groupID, err ? (err as any).code : "nil", err);
    console.log(executor);
    if (err instanceof GroupError && err.code === ErrorCode.mustPause) {
      if (executor.length > 0) {
        console.error("got executor pause condition on group:", groupID);
      }
      return ErrorInstruction.continueWithoutCompleting; // stop the executor
    }
    return ErrorInstruction.shouldContinue;
  }

  async activateNewWorker(): Promise<void> {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) { return; }
    const newWorker = reg.installing || reg.waiting;
    if (!newWorker) {
      console.warn("missing new worker to notify");
      return;
    }
    newWorker.postMessage({cmd: "activate"});
    if (newWorker.state === "installed") { return; }
    return await new bluebird(resolve => {
      newWorker.addEventListener("statechange", () => {
        if (["installed", "activating", "activated"].includes(newWorker.state)) {
          resolve();
        }
      });
    });
  }
  private sendSimpleCmd(cmd: string): void {
    navigator.serviceWorker.controller?.postMessage({cmd: cmd});
  }

  /// Messages sent specifically to this tab
  onClientMessage(event: MessageEvent) {
    const data = event.data as ClientMessage;
    if (!data || !data.cmd) { return; }
    switch (data.cmd) {
      case "newmessage":
        this.delegate?.didReceiveNewMessage();
        break;
      case "startcall":
        this.delegate?.didStartHuddle();
        break;
      case "view-group":
        console.log("view, group", data.groupID, "acct:", data.accountID);
        if (data.accountID && data.groupID) {
          this.delegate?.navigateToGroup(data.accountID, data.groupID);
        }
        break;
      case "answercall":
        console.log("answer, group", data.groupID, "account:", data.accountID);
        if (data.accountID && data.groupID) {
          this.delegate?.doHuddleJoin(data.accountID, data.groupID);
        }
        break;
      case "callanswered":
        this.delegate?.didAnswerHuddle();
        break;
    }
  }

  /// updates from group.services.ts
  async onMessage(event: MessageEvent) {
    const data = event.data as ChannelMessage;
    //console.log("notificationService, worker event:", data);
    if (!data || !data.cmd) { return; }
    const listeners = this.listenerMap.get(data.cmd as ChannelNotificationTypes);
    if (!listeners) { return; }

    const groupMsg = data as ChannelGroupMessage;
    const mtgMsg = data as ChannelMeetingMessage;

    const executor = this.executorForGroup(groupMsg.groupID);
    
    switch (data.cmd) {
      case "newgroup":
      case "group":
      case "delgroup":
      case "thread":
        console.log("group event from service worker, group:", groupMsg.groupID);
        await executor.addAndStart<void>(new Date(), async () => {
          const mapped = listeners.map(fn => {
            return (fn as groupListenerFn)(groupMsg.groupID, groupMsg.msgID);
          });
          await bluebird.all(mapped);
        });
        break;
      case "message":
        console.log("message event from service worker, group:", groupMsg.groupID, "msg:", groupMsg.msgID);
        if (!groupMsg.msgID) { return; }

        await executor.addAndStart<void>(new Date(), async () => {
          const mapped = listeners.map(fn => {
            return (fn as msgListenerFn)(groupMsg.msgID!, groupMsg.parentID, groupMsg.accountID);
          });
          await bluebird.all(mapped);
        });
        break;
      case "delmsg":
        console.log("delmsg event from service worker, group:", groupMsg.groupID, "msg:", groupMsg.msgID);
        if (!groupMsg.msgID) { return; }
        await executor.addAndStart<void>(new Date(), async () => {
          const mapped = listeners.map(fn => {
            return (fn as remMsgListenerFn)(groupMsg.groupID, groupMsg.msgID!, groupMsg.accountID, groupMsg.parentID);
          });
          await bluebird.all(mapped);
        });
        break;
      case "reaction":
        console.log("reaction event from service worker, group:", groupMsg.groupID, "msg:", groupMsg.msgID,
          "user:", groupMsg.userID);
        if (!groupMsg.msgID || !groupMsg.reactionID ||
            !groupMsg.contentID || typeof groupMsg.contenttype !== "number" || !groupMsg.content) {
          return;
        }

        // TODO: add some kind of value mapping from command to number
        await executor.addAndStart<void>(new Date(), async () => {
          const mapped = listeners.map(fn => {
            (fn as reactionListenerFn)(groupMsg.msgID!, groupMsg.reactionID!, groupMsg.userID,
            groupMsg.contentID!);
          });
          await bluebird.all(mapped);
        });
        break;
      case "remReaction":
        console.log("remReaction event from service worker, group:", groupMsg.groupID, "msg:", groupMsg.msgID);
        if (!groupMsg.msgID || !groupMsg.contentID) { return; }
        await executor.addAndStart<void>(new Date(), async () => {
          const mapped = listeners.map(fn => {
            (fn as reactionRemListenerFn)(groupMsg.msgID!, groupMsg.contentID!, groupMsg.userID);
          });
          await bluebird.all(mapped);
        });
        break;
      case "huddleStart":
        await executor.addAndStart<void>(new Date(), async () => {
          const mapped = listeners.map(fn => {
            (fn as huddleListenerFn)(groupMsg.groupID, groupMsg.accountID);
          });
          await bluebird.all(mapped);
        });
        break;
      case "huddleEnd":
        await executor.addAndStart<void>(new Date(), async () => {
          const mapped = listeners.map(fn => {
            (fn as huddleListenerFn)(groupMsg.groupID, groupMsg.accountID);
          });
          await bluebird.all(mapped);
        });
        break;
      case "mtgDecision":
      case "newWaiting":
      case "remWaiting":
        await executor.addAndStart<void>(new Date(), async () => {
          const mapped = listeners.map(fn => {
            (fn as meetingMsgListenerFn)(mtgMsg);
          });
          await bluebird.all(mapped);
        });
        break;
      default:
        console.warn("unknown worker message type:", data);
    }
  }

  // push notification updates for huddle "calling"
  onCallMessage(event: MessageEvent<PushNotificationGroupData | PushNotificationMessageData | HuddleRequestTimeout>) {
    const noteType = event.data.noteType;
    const groupID = event.data.groupID;
    const accountID = event.data.accountID;

    switch (noteType) {
      case NotificationType.HUDDLE_START:
        this.delegate?.showIncomingCall(accountID, groupID, event.data.groupname);
        break;
      case NotificationType.HUDDLE_REQ_RESULT:
        this.delegate?.hideIncomingCall(accountID, groupID);
        break;
      case NotificationType.HUDDLE_REQ_TIMEOUT:
        this.delegate?.hideIncomingCall(accountID, groupID);
        break;
      default:
    }
  }

  askNotificationPermission(): Promise<string> {
    return new bluebird<string>((resolve, reject) => {
      const result = Notification.requestPermission(function (r) {
        resolve(r);
      });
      if (result) {
        result.then(resolve, reject);
      }
    })
    .then(result => {
      if (result !== "granted") {
        throw new Error("Permission Denied");
      }
      return result;
    });
  }

  askSafariNotificationPermission(accessToken: string, deviceID: string): Promise<void> {
    return new bluebird<void>((resolve, reject) => {
      const permData = (window as any).safari.pushNotification.permission(secrets.SAFARI_PUSH_ID);
      /*if (!permData || permData.permission !== "granted") {
        return reject(new Error("Safari Permission Not Granted"));
      }*/
      const url = `https://${http.currentDomain()}/safaripush`;
      console.log("domain:", http.currentDomain(), "token:", permData.deviceToken);
      (window as any).safari.pushNotification.requestPermission(
        url,
        secrets.SAFARI_PUSH_ID, {
          accessToken: accessToken,
          deviceID: deviceID
        }, (permData: any) => {
          if (!permData) {
            console.error("No permData");
            return reject(new Error("No data received"));
          }
          if (permData.permission !== "granted") {
            console.warn("Didn't get push notification permission:", permData);
          } else {
            console.log("permission granted:", permData);
          }
          resolve();
        });
    });
  }

  /*async storeSafariPushToken(deviceID: string): Promise<void> {
    if (!("safari" in window)) { return; }
    console.log("safari push ID:", secrets.SAFARI_PUSH_ID);
    const permData = (window as any).safari.pushNotification.permission(secrets.SAFARI_PUSH_ID);
    if (!permData) { return; }
    if (permData.permission !== "granted") { return; }
    await http.post<ApnsPushInfo, any>(`/${deviceID}/push/apns`, {
      token: permData.deviceToken,
      bundleID: secrets.SAFARI_PUSH_ID,
      pushType: "apns"
    });
  }*/

  async registerWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!("serviceWorker" in navigator)) { return null; }
    try {
      const oldreg = await navigator.serviceWorker.getRegistration("/push/");
      if (oldreg && oldreg.scope.endsWith("push/")) {
        console.log("old service worker:", oldreg);
        oldreg.unregister();
      }
    } catch (err) {
      void err;
      // do nothing
    }
    const reg = await navigator.serviceWorker.register(
      "/js/worker.js", {scope: "/"});

    return reg;
  }


  async updateWorker(): Promise<void> {
    const twoHoursAgo = moment().subtract(2, "hour");
    if (secrets.ENV !== "dev" && this.lastUpdateCheck && twoHoursAgo.isBefore(this.lastUpdateCheck)) {
      return;
    }
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
      reg.update();
      this.lastUpdateCheck = new Date();
    }
  }

  async subscribe(registration: ServiceWorkerRegistration | null): Promise<PushSubscription | ApnsPushInfo | undefined> {
    if (!registration) { return undefined; }
    if (!("pushManager" in registration)) {
      if ("safari" in window) {
        const permData = (window as any).safari.pushNotification.permission(secrets.SAFARI_PUSH_ID);
        if (!permData) { return undefined; }
        if (permData.permission !== "granted") { return undefined; }
        return {
          deviceToken: permData.deviceToken,
          bundleID: secrets.SAFARI_PUSH_ID,
          pushType: "apns"
        };
      }
      return undefined;
    }
    const serviceWorker: ServiceWorker | null = registration.active;
    if (!serviceWorker) {
      console.error("no service worker found in any state");
      return undefined;
    }
    if (serviceWorker.state !== "activated") {
      return undefined;
    }
    return await this.doSubscribe(registration);
  }

  async doSubscribe(registration: ServiceWorkerRegistration): Promise<PushSubscription> {
    const cur = await registration.pushManager.getSubscription();
    if (cur) {
      const val = window.localStorage.getItem("sc.pushversion");
      if (!val || val !== "1") {
        console.log("resubscribing for notifications");
        await cur.unsubscribe();
      }
    }

    const sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: secrets.VAPID_PUB_KEY
    });
    window.localStorage.setItem("sc.pushversion", "1");
    return sub;
  }

  saveSubscription(subscription: PushSubscription): Promise<void> {
    return http.post<PushSubscription, void>(
      "/device/push_subscription/web", subscription);
  }
}

let workerService: WorkerService | undefined;

export function workerServiceFactory(): WorkerService {
  if (!workerService) {
    workerService = new WorkerServiceImpl();
  }
  return workerService;
}
